import { useEffect, useRef, useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../lib/query-client'

/**
 * SSE message types from backend streaming service
 */
export interface SSEMessage {
  type: 'connection' | 'agent_progress' | 'error' | 'final_state' | 'log'
  message: string
  fileId?: string
  timestamp?: string
  data?: unknown
  level?: 'info' | 'warn' | 'error' | 'debug'
  agent?: string
}

type ConnectionState = 'connecting' | 'connected' | 'closed' | 'error'

interface UseSSEOptions {
  /** Enable/disable the SSE connection */
  enabled?: boolean
  /** Auto-close on final_state message */
  autoCloseOnComplete?: boolean
}

/**
 * Hook for Server-Sent Events connection with automatic cleanup.
 *
 * CRITICAL: This hook handles EventSource cleanup to prevent memory leaks.
 * The connection is closed when:
 * - Component unmounts
 * - enabled becomes false
 * - autoCloseOnComplete is true and final_state received
 *
 * @param fileId - The run's file ID
 * @param options - SSE options
 * @returns Messages array, connection state, and manual close function
 */
export function useSSE(fileId: string | undefined, options: UseSSEOptions = {}) {
  const { enabled = true, autoCloseOnComplete = true } = options

  const [messages, setMessages] = useState<SSEMessage[]>([])
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting')
  const eventSourceRef = useRef<EventSource | null>(null)
  const queryClient = useQueryClient()

  // Manual close function
  const close = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
      setConnectionState('closed')
    }
  }, [])

  useEffect(() => {
    // Don't connect if disabled or no fileId
    if (!enabled || !fileId) {
      return
    }

    // Close any existing connection before creating new one
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    setConnectionState('connecting')
    setMessages([])

    // Create new EventSource connection
    // Note: withCredentials is not directly supported by EventSource,
    // but same-origin requests include cookies automatically
    const url = `/api/v1/stream/${fileId}`
    const eventSource = new EventSource(url)
    eventSourceRef.current = eventSource

    eventSource.onopen = () => {
      setConnectionState('connected')
    }

    eventSource.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as SSEMessage
        setMessages((prev) => [...prev, message])

        // Invalidate run detail cache when status changes
        if (message.type === 'final_state') {
          queryClient.invalidateQueries({
            queryKey: queryKeys.runs.detail(fileId),
          })
          queryClient.invalidateQueries({
            queryKey: queryKeys.runs.lists(),
          })

          // Auto-close if enabled
          if (autoCloseOnComplete) {
            eventSource.close()
            eventSourceRef.current = null
            setConnectionState('closed')
          }
        }
      } catch (err) {
        console.error('Failed to parse SSE message:', err)
      }
    }

    eventSource.onerror = () => {
      // Only set error state if connection was not intentionally closed
      if (eventSource.readyState === EventSource.CLOSED) {
        setConnectionState('closed')
      } else {
        setConnectionState('error')
      }
      // Browser will auto-reconnect unless we explicitly close
    }

    // CRITICAL: Cleanup function prevents memory leaks
    return () => {
      eventSource.close()
      eventSourceRef.current = null
      setConnectionState('closed')
    }
  }, [fileId, enabled, autoCloseOnComplete, queryClient])

  return {
    messages,
    connectionState,
    close,
    isConnected: connectionState === 'connected',
    isConnecting: connectionState === 'connecting',
  }
}
